import { FilterGroupType } from '../Components/ProductFiltering/types/FilterGroupType';
import {
  PRODUCT_FILTERING_RECEIVE,
  PRODUCT_FILTERING_UPDATE_FILTERS,
  PRODUCT_FILTERING_SET_SORTING,
  PRODUCT_FILTERING_TOGGLE_FILTERBAR,
  PRODUCT_FILTERING_UPDATE_PENDING,
  PRODUCT_FILTERING_SET_SHOW_ALL,
  PRODUCT_FILTERING_SET_OPEN,
} from '../constants';
import { get } from '../Services/http';
import {
  setIsLoadingProductListing,
  setProducts,
} from './ProductListing.action';

let abortController = null;

export const queryProductFiltering =
  (
    productFilteringState = null,
    isInitialLoad = false,
    fromFilter = false,
    keepAllUrlParams = false
  ) =>
  async (dispatch, getState) => {
    try {
      if (productFilteringState == null) {
        productFilteringState = getState().productFiltering;
      }
      const productListingState = getState().productListing;

      dispatch(toggleUpdatePending(true));
      // Show skeleton cards before loading the new products
      if (!isInitialLoad) {
        dispatch(setIsLoadingProductListing(true));
        dispatch(
          setProducts(Array.from({ length: productListingState.pageSize }))
        );
      }

      // Cancel the last request
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();

      const queryString = getProductFilteringAsQueryString(
        productFilteringState,
        isInitialLoad || keepAllUrlParams,
        fromFilter
      );
      const response = await get(
        `/api/productfiltering?${queryString}`,
        abortController
      );

      if (response.ok) {
        const isJson = response.headers
          .get('content-type')
          ?.includes('application/json');
        if (isJson) {
          let data = await response.json();
          dispatch(
            receiveProductFiltering(data, isInitialLoad, keepAllUrlParams)
          );
        }
      }
    } catch (err) {
      // TODO Error handling, not in MOX-86
      console.log(err);
    } finally {
      dispatch(setIsLoadingProductListing(false));
      dispatch(toggleUpdatePending(false));
    }
  };

export const receiveProductFiltering = (
  productFiltering,
  isInitialLoad = undefined,
  keepAllUrlParams = false
) => ({
  type: PRODUCT_FILTERING_RECEIVE,
  payload: productFiltering,
  isInitialLoad,
  keepAllUrlParams,
});

export const getProductFilteringAsQueryString = (
  productFilteringState,
  keepAllUrlParams = false,
  fromFilter = false
) => {
  const currentSearchParams = new URLSearchParams(location.search);
  const searchParams = new URLSearchParams(
    keepAllUrlParams ? location.search : undefined
  );

  // Keep certain params from the current query
  if (!keepAllUrlParams) {
    const paramsToKeep = ['page', 'q'];
    paramsToKeep.forEach(
      (key) =>
        currentSearchParams.has(key) &&
        currentSearchParams.get(key) != '' &&
        searchParams.append(key, currentSearchParams.get(key))
    );

    if (
      productFilteringState.filters?.every(
        (filter) => filter.selectedOptions.length == 0
      )
    ) {
      searchParams.delete('q');
    }
  }

  if (!fromFilter && searchParams.has('ff')) {
    searchParams.delete('ff');
  }

  // Loop each filter, add each selected option, checkboxes
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.selectedOptions.length > 0 &&
        f.filterGroupType != FilterGroupType.Range
    )
    .forEach((filter) =>
      filter.selectedOptions.forEach((selectedOption) => {
        searchParams.append(filter.id, selectedOption);
      })
    );

  // Range filters
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.filterGroupType == FilterGroupType.Range &&
        f.selectedOptions.length == 2
    )
    .forEach((filter) => {
      searchParams.append(
        filter.id,
        `${filter.selectedOptions[0]}-${filter.selectedOptions[1]}`
      );
    });

  // Sorting
  const selectedSorting =
    productFilteringState.sortCriteria?.sortItems?.find((s) => s.selected);

  if (selectedSorting && selectedSorting.query) {
    searchParams.set('sort_by', selectedSorting.sortBy);
    searchParams.set('sort_direction', selectedSorting.sortDirection);
  }

  if (fromFilter) {
    searchParams.append('ff', fromFilter.toString());
  }

  return searchParams.toString();
};

export const onOptionChanged =
  (filterId, optionId) => (dispatch, getState) => {
    const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
    const filterIndex = filters.findIndex((f) => f.id == filterId);
    if (filterIndex == -1) return;

    const { singleSelect, selectedOptions } = filters[filterIndex];
    const optionIndex = selectedOptions.indexOf(optionId);
    const isOptionSelected = optionIndex != -1;
    let updatedSelectedOptions = [];

    if (singleSelect) {
      updatedSelectedOptions = isOptionSelected ? [optionId] : [];
    } else {
      updatedSelectedOptions = isOptionSelected
        ? [
            // selected, remove it from the list
            ...selectedOptions.slice(0, optionIndex),
            ...selectedOptions.slice(optionIndex + 1),
          ]
        : [...selectedOptions, optionId]; // Not selected, add it to selected list
    }
    filters[filterIndex].selectedOptions = updatedSelectedOptions;

    dispatch(updateProductFilteringFilters(filters));
  };

export const onRangeChanged =
  (filterId, values) => (dispatch, getState) => {
    const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
    const filterIndex = filters.findIndex((f) => f.id == filterId);
    if (
      filterIndex == -1 ||
      filters[filterIndex].filterGroupType != FilterGroupType.Range
    )
      return;

    filters[filterIndex].selectedOptions = values;

    dispatch(updateProductFilteringFilters(filters));
  };

const updateProductFilteringFilters = (filters) => ({
  type: PRODUCT_FILTERING_UPDATE_FILTERS,
  payload: filters,
});

export const toggleDesktopFilterBar = (showDesktopFilters) => ({
  type: PRODUCT_FILTERING_TOGGLE_FILTERBAR,
  payload: {
    showDesktopFilters,
  },
});

export const toggleUpdatePending = (updatePending) => ({
  type: PRODUCT_FILTERING_UPDATE_PENDING,
  payload: {
    updatePending,
  },
});

export const onSortItemSelected = (sortItem) => (dispatch) => {
  dispatch({
    type: PRODUCT_FILTERING_SET_SORTING,
    payload: sortItem,
  });
  dispatch(queryProductFiltering());
};

export const clearAllFilters = () => (dispatch, getState) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);
  for (let filter of filtersCopy) {
    filter.selectedOptions = [];
  }
  dispatch(
    queryProductFiltering({
      ...productFilteringState,
      filters: filtersCopy,
    })
  );
};

export const clearFilterGroup =
  (filterGroupId) => (dispatch, getState) => {
    const productFilteringState = getState().productFiltering;
    const filtersCopy = Object.assign([], productFilteringState.filters);
    const filterGroup = filtersCopy.find((f) => f.id == filterGroupId);
    if (!filterGroup) return;
    filterGroup.selectedOptions = [];
    dispatch(
      queryProductFiltering({
        ...productFilteringState,
        filters: filtersCopy,
      })
    );
  };

export const clearFilter =
  (selectedFilter, selectedFilterGroupId) => (dispatch, getState) => {
    const productFilteringState = getState().productFiltering;
    const filtersCopy = Object.assign([], productFilteringState.filters);
    const selectedFilterGroup = filtersCopy.find(
      (g) => g.id == selectedFilterGroupId
    );
    if (!selectedFilterGroup) return;
    const selectedFilterGroupSelectedOptions =
      selectedFilterGroup.selectedOptions;
    const clickedFilterIndex =
      selectedFilterGroupSelectedOptions.indexOf(selectedFilter);
    selectedFilterGroupSelectedOptions <= 1
      ? []
      : selectedFilterGroupSelectedOptions.splice(clickedFilterIndex, 1);
    selectedFilterGroup.selectedOptions =
      selectedFilterGroupSelectedOptions;
    dispatch(
      queryProductFiltering({
        ...productFilteringState,
        filters: filtersCopy,
      })
    );
  };

export const toggleShowAllForFilter =
  (filterId) => (dispatch, getState) => {
    const { showAllForFilters = [] } = getState().productFiltering;
    const localShowAllForFilters = [...showAllForFilters];
    const index = localShowAllForFilters.indexOf(filterId);
    if (index == -1) {
      localShowAllForFilters.push(filterId);
    } else {
      localShowAllForFilters.splice(index, 1);
    }
    return dispatch({
      type: PRODUCT_FILTERING_SET_SHOW_ALL,
      payload: localShowAllForFilters,
    });
  };

export const setFilterOpen = (filterId, open) => (dispatch, getState) => {
  const { openFilters = [] } = getState().productFiltering;
  const localOpenFilters = [...openFilters];
  const index = localOpenFilters.indexOf(filterId);
  if (open && index == -1) {
    localOpenFilters.push(filterId);
  } else if (!open && index != -1) {
    localOpenFilters.splice(index, 1);
  }
  return dispatch({
    type: PRODUCT_FILTERING_SET_OPEN,
    payload: localOpenFilters,
  });
};
